import React, { useEffect, useState } from "react";
import { ABOUT_ME_URL, UPDATE_ME_URL } from "api/UserConstants";
import PageHeader from "components/common/PageHeader";
import { Container, Grid, TextField, Button, Typography, InputAdornment, IconButton } from "@mui/material";
import * as yup from 'yup';
import { useFormik, Formik } from 'formik';
import AppActions from 'components/app/actions';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const validationSchema = yup.object({
    firstname: yup
        .string('Enter your first name')
        .min(2, 'First name should be of minimum 2 characters length')
        .required('First name is required'),
    lastname: yup
        .string('Enter your last name')
        .min(2, 'Last name should be of minimum 2 characters length')
        .required('Last name is required'),
    email: yup
        .string('Enter your email')
        .email('Enter a valid email')
        .required('Email is required'),
    title: yup
        .string('Enter your title')
        .min(2, 'Title should be of minimum 2 characters length')
        .required('Title is required'),
    affiliation: yup
        .string('Enter your affiliation')
        .min(2, 'Affiliation should be of minimum 2 characters length')
        .required('Affiliation is required'),

});

const passwordValidationSchema = yup.object({
    currentPassword: yup
        .string('Enter your current password')
        .required('Password is required'),
    newPassword: yup
        .string('Enter your new password')
        .min(8, 'Password should be of minimum 8 characters length')
        .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/, 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'),
    confirmPassword: yup
        .string('Confirm your password')
        .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
        .required('Password is required'),
});


export const ProfileContainer = () => {
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const { setToastMessage } = AppActions();

    const formik = useFormik({
        initialValues: {
            firstname: '',
            lastname: '',
            email: '',
            title: '',
            affiliation: '',
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            updateUser(values);
        }
    });

    const passwordFormik = useFormik({
        initialValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
        validationSchema: passwordValidationSchema,
        onSubmit: (values) => {
            updatePassword(values);
        }
    });

    const updatePassword = (values) => {
        const updatedPassword = {
            current_password: values.currentPassword,
            new_password: values.newPassword,
        };

        fetch(UPDATE_ME_URL,
            {
                method: 'PUT',
                body: JSON.stringify(updatedPassword),
                headers: {
                    'Content-Type': 'application/json',
                }
            }
        )
            .then((response) => response.json())
            .then((data) => {
                if (data.id && !data.message) {
                    setToastMessage({
                        message: 'Password updated successfully',
                        type: 'success',
                    });
                    passwordFormik.resetForm();
                }
                else {
                    throw new Error(data.message);
                }
            })
            .catch((error) => {
                console.error('Error:', error);
                setToastMessage({
                    message: error.message,
                    type: 'error',
                });
            })
    };



    const updateUser = (values) => {
        const updatedUser = {
            firstname: values.firstname,
            lastname: values.lastname,
            email: values.email,
            title: values.title,
            affiliation: values.affiliation,
        };

        fetch(UPDATE_ME_URL,
            {
                method: 'PUT',
                body: JSON.stringify(updatedUser),
                headers: {
                    'Content-Type': 'application/json',
                }
            }
        )
            .then((response) => response.json())
            .then((data) => {
                if (data.id && !data.message) {
                    setToastMessage({
                        message: 'Profile updated successfully',
                        type: 'success',
                    });
                }
                else {
                    throw new Error(data.message);
                }
            })
            .catch((error) => {
                console.error('Error:', error);
                setToastMessage({
                    message: error.message,
                    type: 'error',
                });
            })
    };

    useEffect(() => {
        fetch(`${ABOUT_ME_URL}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((response) => response.json())
            .then((data) => {
                data = data.data;
                formik.setValues({
                    firstname: data.firstname,
                    lastname: data.lastname,
                    email: data.email,
                    title: data.title || '',
                    affiliation: data.affiliation || '',
                });
            });
    }
        , []);

    return (
        <Container>
            <PageHeader title="Profile" subTitle="Manage your profile" />
            <Formik onSubmit={formik.handleSubmit}>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} md={5}>
                        <Typography variant="h6">Personal Details</Typography>
                        <Typography variant="body2" color="textSecondary">Update your account's profile information</Typography>
                    </Grid>
                    <Grid container item xs={12} md={7} spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                id="firstname"
                                name="firstname"
                                label="First Name"
                                value={formik.values.firstname}
                                onChange={formik.handleChange}
                                error={formik.touched.firstname && Boolean(formik.errors.firstname)}
                                helperText={formik.touched.firstname && formik.errors.firstname}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                id="lastname"
                                name="lastname"
                                label="Last Name"
                                value={formik.values.lastname}
                                onChange={formik.handleChange}
                                error={formik.touched.lastname && Boolean(formik.errors.lastname)}
                                helperText={formik.touched.lastname && formik.errors.lastname}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled
                                fullWidth
                                id="email"
                                name="email"
                                label="Email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                helperText={formik.touched.email ? formik.errors.email : 'Email cannot be updated'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                id="title"
                                name="title"
                                label="Title"
                                value={formik.values.title}
                                onChange={formik.handleChange}
                                error={formik.touched.title && Boolean(formik.errors.title)}
                                helperText={formik.touched.title && formik.errors.title}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                id="affiliation"
                                name="affiliation"
                                label="Affiliation"
                                value={formik.values.affiliation}
                                onChange={formik.handleChange}
                                error={formik.touched.affiliation && Boolean(formik.errors.affiliation)}
                                helperText={formik.touched.affiliation && formik.errors.affiliation}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} style={{ justifyContent: 'flex-end', display: 'flex' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={formik.handleSubmit}
                            type="submit"
                        >
                            SAVE
                        </Button>
                    </Grid>
                </Grid>
            </Formik>
            <Formik onSubmit={passwordFormik.handleSubmit}>
                <Grid container spacing={2} style={{ marginTop: '2rem' }}>
                    <Grid item xs={12} md={5}>
                        <Typography variant="h6">Change Password</Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ pr: 12 }}>Ensure your account is using a long, random password to stay secure.</Typography>
                    </Grid>
                    <Grid container item xs={12} md={7} spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                autoComplete="current-password"
                                id="currentPassword"
                                name="currentPassword"
                                label="Current Password"
                                type={showCurrentPassword ? 'text' : 'password'}
                                value={passwordFormik.values.currentPassword}
                                onChange={passwordFormik.handleChange}
                                error={passwordFormik.touched.currentPassword && Boolean(passwordFormik.errors.currentPassword)}
                                helperText={passwordFormik.touched.currentPassword && passwordFormik.errors.currentPassword}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowCurrentPassword(prev => !prev)}
                                                edge="end"
                                                aria-label="toggle current password visibility"
                                            >
                                                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                autoComplete="new-password"
                                id="newPassword"
                                name="newPassword"
                                label="New Password"
                                type={showNewPassword ? 'text' : 'password'}
                                value={passwordFormik.values.newPassword}
                                onChange={passwordFormik.handleChange}
                                error={passwordFormik.touched.newPassword && Boolean(passwordFormik.errors.newPassword)}
                                helperText={passwordFormik.touched.newPassword && passwordFormik.errors.newPassword}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowNewPassword(prev => !prev)}
                                                edge="end"
                                                aria-label="toggle new password visibility"
                                            >
                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                autoComplete="new-password"
                                id="confirmPassword"
                                name="confirmPassword"
                                label="Confirm Password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={passwordFormik.values.confirmPassword}
                                onChange={passwordFormik.handleChange}
                                error={passwordFormik.touched.confirmPassword && Boolean(passwordFormik.errors.confirmPassword)}
                                helperText={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowConfirmPassword(prev => !prev)}
                                                edge="end"
                                                aria-label="toggle confirm password visibility"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} style={{ justifyContent: 'flex-end', display: 'flex' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={passwordFormik.handleSubmit}
                            type="submit"
                        >
                            SAVE
                        </Button>
                    </Grid>
                </Grid>
            </Formik>
        </Container>
    )

}